// html-to-draftjs only recognizes <strong>/<em>/<ins>/<del> for bold/italic/
// underline/strikethrough — not the <b>/<i>/<u>/<strike> tags Word (and most
// other sources) actually paste. Without renaming these, formatting silently
// disappears on paste.
const TAG_RENAME_MAP = {
  b: "strong",
  i: "em",
  u: "ins",
  strike: "del",
};

const stripWordNoise = (html) =>
  html
    .replace(/<!--\[if[\s\S]*?<!\[endif\]-->/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<xml[\s\S]*?<\/xml>/gi, "")
    .replace(/<o:p>\s*<\/o:p>/gi, "");

const renameElement = (doc, element, newTagName) => {
  const replacement = doc.createElement(newTagName);
  Array.from(element.attributes).forEach((attr) =>
    replacement.setAttribute(attr.name, attr.value)
  );
  while (element.firstChild) replacement.appendChild(element.firstChild);
  element.parentNode.replaceChild(replacement, element);
};

// Word wraps its clipboard HTML source across many lines for readability
// (e.g. "No\n     menu lateral..."). Browsers normally collapse that kind of
// whitespace away when rendering, but html-to-draftjs copies text nodes
// as-is, and the editor renders with `white-space: pre-wrap` — so those
// cosmetic line breaks turn into real line breaks in the middle of a
// sentence. Collapse whitespace runs the same way normal HTML flow would.
const collapseWhitespace = (node) => {
  Array.from(node.childNodes).forEach((child) => {
    if (child.nodeType === 3) {
      child.textContent = child.textContent.replace(/\s+/g, " ");
    } else {
      collapseWhitespace(child);
    }
  });
};

// Word writes font sizes in points (e.g. "font-size:18.0pt"). html-to-draftjs
// only strips a trailing "px" when building its "fontsize-X" style key, and
// react-draft-wysiwyg applies it via `+value` (Number conversion) — so a
// leftover "pt" suffix turns into NaN and the size silently doesn't apply.
//
// Beyond that, the WYSIWYG editor can only visually render a font size that
// exists in its own toolbar's `fontSize.options` list — that's the fixed set
// react-draft-wysiwyg builds its custom style map from. A size outside that
// list still saves/renders fine wherever the raw HTML is read directly (the
// client view), but looks like plain text while editing. Snap the converted
// value to the nearest supported option so what you see while editing
// matches what gets saved.
const POINTS_TO_PIXELS = 96 / 72;
const nearestOption = (value, options) =>
  options.reduce((closest, option) =>
    Math.abs(option - value) < Math.abs(closest - value) ? option : closest
  );

const convertPointFontSizes = (doc, sizeOptions) => {
  Array.from(doc.body.querySelectorAll('[style*="font-size"]')).forEach(
    (element) => {
      const match = (element.style.fontSize || "").match(/^([\d.]+)pt$/i);
      if (match) {
        const px = parseFloat(match[1]) * POINTS_TO_PIXELS;
        const snapped =
          sizeOptions && sizeOptions.length
            ? nearestOption(px, sizeOptions)
            : Math.round(px);
        element.style.fontSize = `${snapped}px`;
      }
    }
  );
};

export const sanitizeWordHtml = (html, fontSizeOptions) => {
  const doc = new DOMParser().parseFromString(stripWordNoise(html), "text/html");

  Object.keys(TAG_RENAME_MAP).forEach((tag) => {
    Array.from(doc.body.querySelectorAll(tag)).forEach((element) =>
      renameElement(doc, element, TAG_RENAME_MAP[tag])
    );
  });

  convertPointFontSizes(doc, fontSizeOptions);
  collapseWhitespace(doc.body);

  return doc.body.innerHTML;
};
